import { Component } from "@theme/component";
import { QuantitySelectorUpdateEvent } from "@theme/events";

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /** @type {boolean} */
  #hasValueChanged = false;

  connectedCallback() {
    super.connectedCallback();

    // Track when user actually types in the input
    this.refs.quantityInput.addEventListener("input", () => {
      this.#hasValueChanged = true;
    });
  }
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#hasValueChanged = true;
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const min = Number(quantityInput.min) || 0;

    // Cart line met een minimum (bv. min_qty 4): stepDown() blijft op min hangen.
    // Net als bij min 0 → 0 betekent "onder het minimum" hier: regel verwijderen.
    if (this.#isCartLine && min > 0 && Number(quantityInput.value) <= min) {
      quantityInput.value = "0";
      this.#hasValueChanged = true;
      this.#onQuantityChange({ allowZero: true });
      return;
    }

    quantityInput.stepDown();
    this.#hasValueChanged = true;
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput)
      return;

    // Use setTimeout to avoid interfering with focus management
    setTimeout(() => {
      quantityInput.select();
    }, 0);

    // Reset the change flag when focusing (user might just be navigating)
    this.#hasValueChanged = false;
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      const oldValue = this.refs.quantityInput.value;
      const newValue = event.target.value;

      // Only trigger change if value actually changed or was changed by user interaction
      if (oldValue !== newValue || this.#hasValueChanged) {
        this.refs.quantityInput.value = newValue;
        // Een expliciet getypte 0 op een cart line = regel verwijderen (ook bij min > 0).
        this.#onQuantityChange({ allowZero: this.#isCartLine && Number(newValue) === 0 });
      }

      // Reset the flag after processing
      this.#hasValueChanged = false;
    }
  }

  /**
   * Whether this selector sits on a cart line (vs. a product form).
   * @returns {boolean}
   */
  get #isCartLine() {
    return Boolean(this.refs.quantityInput.dataset.cartLine);
  }

  /**
   * Handles the quantity change event.
   * @param {{ allowZero?: boolean }} [options] - allowZero: keep 0 as-is (cart line removal).
   */
  #onQuantityChange({ allowZero = false } = {}) {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules(allowZero);
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }

  /**
   * Checks the quantity rules are met: snap naar min + n·step, clamp op min/max.
   * @param {boolean} [allowZero] - 0 ongemoeid laten (cart line verwijderen).
   */
  #checkQuantityRules = (allowZero = false) => {
    const { quantityInput } = this.refs;
    const min = Number(quantityInput.min) || 0;
    const max = Number(quantityInput.max) || 0;
    const step = Number(quantityInput.step) || 1;
    let value = Number(quantityInput.value) || 0;

    if (allowZero && value === 0) {
      quantityInput.value = "0";
      return;
    }

    if (step > 1) {
      value = min + Math.round((value - min) / step) * step;
    }
    if (min && value < min) value = min;
    if (max && value > max) value = max;

    quantityInput.value = String(value);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get("quantity-selector-component")) {
  customElements.define("quantity-selector-component", QuantitySelectorComponent);
}
